import React from 'react';
import { ButtonVariant } from 'components/commercetools-ui/atoms/button';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import MlVideoPlayer from 'components/commercetools-ui/molecules/video-player';
import useClassNames from 'helpers/hooks/useClassNames';
import { renderTextWithLineBreaks } from 'helpers/utils/textFormatter';
import { Reference } from 'types/reference';

type MediaPosition = 'media-top' | 'media-bottom' | 'media-left' | 'media-right';
type Theme = 'dark' | 'white';

interface ContentStripProps {
  title: string;
  description?: string;
  buttonText?: string;
  buttonLink?: Reference;
  buttonVariant?: ButtonVariant;
  mediaType: 'video' | 'image';
  mediaSrc?: string;
  image?: ImageProps;
  mobilePosition?: MediaPosition;
  tabletPosition?: MediaPosition;
  desktopPosition?: MediaPosition;
  theme?: Theme;
  className?: string;
}

const ContentStrip: React.FC<ContentStripProps> = ({
  title,
  description,
  buttonText,
  buttonLink,
  buttonVariant = 'primary',
  mediaType,
  mediaSrc,
  image,
  mobilePosition = 'media-top',
  tabletPosition = 'media-right',
  desktopPosition = 'media-right',
  theme = 'white',
  className = '',
}) => {
  const isMobileVertical = mobilePosition === 'media-top' || mobilePosition === 'media-bottom';
  const isTabletVertical = tabletPosition === 'media-top' || tabletPosition === 'media-bottom';
  const isDesktopVertical = desktopPosition === 'media-top' || desktopPosition === 'media-bottom';

  const containerClassName = useClassNames([theme === 'dark' ? 'bg-inverted text-white' : 'bg-white', className]);

  const innerContainerClassName = useClassNames([
    'flex container',
    isMobileVertical ? 'flex-col' : 'flex-row items-center',
    'gap-32 py-32',

    isTabletVertical ? 'md:flex-col' : 'md:flex-row md:items-center',
    'md:gap-32 md:py-32',

    isDesktopVertical ? 'lg:flex-col' : 'lg:flex-row lg:items-center',
    'lg:gap-32 lg:py-64',
  ]);

  // Content order and width classes
  const contentClassName = useClassNames([
    'flex flex-col',
    mobilePosition === 'media-top' ? 'order-2' : 'order-1',
    'w-full',

    tabletPosition === 'media-top' ? 'md:order-2' : tabletPosition === 'media-left' ? 'md:order-2' : 'md:order-1',
    isTabletVertical ? 'md:w-full' : 'md:w-1/2',

    desktopPosition === 'media-top' ? 'lg:order-2' : desktopPosition === 'media-left' ? 'lg:order-2' : 'lg:order-1',
    isDesktopVertical ? 'lg:w-full' : 'lg:w-1/2',
  ]);

  // Media order and width classes
  const mediaClassName = useClassNames([
    mobilePosition === 'media-top' ? 'order-1' : 'order-2',
    'w-full',

    tabletPosition === 'media-top' ? 'md:order-1' : tabletPosition === 'media-left' ? 'md:order-1' : 'md:order-2',
    isTabletVertical ? 'md:w-full' : 'md:w-1/2',

    desktopPosition === 'media-top' ? 'lg:order-1' : desktopPosition === 'media-left' ? 'lg:order-1' : 'lg:order-2',
    isDesktopVertical ? 'lg:w-full' : 'lg:w-1/2',
  ]);

  return (
    <section className={containerClassName}>
      <div className={innerContainerClassName}>
        {/* Text Content */}
        <div className={contentClassName} data-testid="content-container">
          <div className="flex flex-col gap-16 md:gap-24">
            {title && <h2 className="body-text-2xlarge font-medium">{title}</h2>}
            {description && (
              <div className="description-container">
                {renderTextWithLineBreaks(description, 'body-text-large leading-6')}
              </div>
            )}
          </div>

          {buttonText && buttonLink && (
            <div className="mt-32 md:mt-24 lg:mt-48">
              <Link buttonVariant={buttonVariant} link={buttonLink} className="inline-block">
                {buttonText}
              </Link>
            </div>
          )}
        </div>

        {/* Media Content (Video or Image) */}
        <div className={mediaClassName} data-testid="media-container">
          {mediaType === 'video' && mediaSrc && <MlVideoPlayer videoId={mediaSrc} />}

          {mediaType === 'image' && image && (
            <div className="relative h-[19.4rem] w-full md:h-[19.4rem] lg:h-[31.9rem]">
              <Image {...image} fill style={{ objectFit: 'cover' }} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContentStrip;
